import { Controller, useFormState } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import ReactSelect from 'dashboard/components/ReactSelect';
import Form from 'dashboard/layouts/FormLayout/Form';
import {
  ModalBody,
  ModalContainer,
  ModalDescription,
  ModalHeader,
} from 'dashboard/layouts/ModalLayout';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import { API_KEY_INFLECTION_LABEL_MAP, ApiKeyInflection, ApiVersion } from 'lib/constants/api';
import { EventName } from 'lib/constants/events';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { useFormSecurely } from 'lib/security/reactHookForm';
import isValidUrl from 'lib/utils/formValidation/isValidUrl';

interface FormCreateWebhookProps {
  defaultValues?: FormCreateWebhookData;
  isOpen: boolean;
  handleSubmit: (data: FormCreateWebhookData) => void;
  handleCancel?: () => void;
  handleCloseModal: () => void;
}

export const FormCreateWebhook = ({
  defaultValues,
  isOpen,
  handleSubmit,
  handleCancel = () => null,
  handleCloseModal,
}: FormCreateWebhookProps) => {
  const {
    register,
    handleSubmit: handleReactHookFormSubmit,
    control,
    reset,
  } = useFormSecurely<FormCreateWebhookData>({
    mode: 'onBlur',
    defaultValues: {
      'api-key-inflection': ApiKeyInflection.Kebab,
      'enabled-events': [],
      ...defaultValues,
    },
  });

  const showAccountUpdatedFieldsEvent = useFeatureFlag(
    'dashboard:accounts-emit-account-updated-field-events-v2',
  );
  const { isSubmitting, isDirty, errors } = useFormState({
    control,
  });
  const disabled = isSubmitting;

  const closeModal = () => {
    handleCloseModal();
    reset();
  };

  const submit = handleReactHookFormSubmit((data: FormCreateWebhookData) => {
    handleSubmit(data);
    closeModal();
  });

  const cancel = () => {
    handleCancel();
    closeModal();
  };

  const apiKeyInflectionOptions = Object.values(ApiKeyInflection).map((apiKeyInflection) => (
    <Option value={apiKeyInflection} key={apiKeyInflection}>
      {API_KEY_INFLECTION_LABEL_MAP[apiKeyInflection]}
    </Option>
  ));

  return (
    <ModalWrapper isOpen={isOpen} handleCloseModal={closeModal}>
      <Form
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
      >
        <ModalContainer data-test='form--create-webhook__modal'>
          <ModalHeader data-test='form--create-webhook__modal__header' onClose={closeModal}>
            Create webhook
          </ModalHeader>

          <ModalDescription>
            <a href='https://docs.withpersona.com/docs/webhooks' target='_blank' rel='noreferrer'>
              Learn more about webhooks here.
            </a>
          </ModalDescription>

          <ModalBody>
            <Label htmlFor='form--create-webhook__input--url' size='lg' weight='bold'>
              URL
            </Label>
            <Spacer height={1} />
            <Input
              {...register('url', {
                validate: (v) => {
                  if (isValidUrl(v)) {
                    return true;
                  } else {
                    return 'Please enter a valid HTTPS URL';
                  }
                },
              })}
              id='form--create-webhook__input--url'
              data-test='form--create-webhook__input--url'
              type='text'
              placeholder='URL'
              hasError={!!errors['url']}
              disabled={disabled}
            />
            {errors['url'] && <FormError>{errors['url'].message}</FormError>}

            <Spacer height={4} />

            <Label htmlFor='form--create-webhook__select--enabled-events' size='lg' weight='bold'>
              Enabled events
            </Label>
            <Spacer height={1} />
            <Controller
              control={control}
              name='enabled-events'
              rules={{
                validate: (v) => {
                  if (v.length > 0) {
                    return true;
                  } else {
                    return 'Please choose one or more events';
                  }
                },
              }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <ReactSelect
                  inputRef={ref}
                  value={value}
                  options={Object.values(EventName)
                    .filter(
                      // TODO (FLE-288): remove this filter when `inquiry-run.conditional-routed` is removed
                      (event) => event !== EventName.InquiryRunConditionalRouted,
                    )
                    .filter(
                      // TODO (ACT-27): remove feature flag for `account.updated-field`` event
                      (event) =>
                        showAccountUpdatedFieldsEvent || event !== EventName.AccountUpdatedFields,
                    )
                    .map((key) => ({ label: key, value: key }))}
                  disabled={disabled}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder='No events selected'
                  hasError={!!errors['enabled-events']}
                  portal={true}
                  isMulti={true}
                  isSearchable={true}
                />
              )}
            />
            {errors['enabled-events'] && <FormError>{errors['enabled-events'].message}</FormError>}

            <Spacer height={4} />

            <Label
              htmlFor='form--create-webhook__select--api-key-inflection'
              size='lg'
              weight='bold'
            >
              Key inflection
            </Label>
            <Spacer height={1} />
            <Controller
              name='api-key-inflection'
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  id='form--create-webhook__select--api-key-inflection'
                  data-test='form--create-webhook__select--api-key-inflection'
                  disabled={disabled}
                  portal={false}
                >
                  {apiKeyInflectionOptions}
                </Select>
              )}
            />
            {errors['api-key-inflection'] && (
              <FormError>{errors['api-key-inflection'].message}</FormError>
            )}

            <Spacer height={4} />

            <HStack equal={true}>
              <Button
                type='button'
                variant='basic'
                onClick={cancel}
                data-test='form--create-webhook__button--cancel'
              >
                Cancel
              </Button>
              <Button
                type='button'
                disabled={disabled || !isDirty}
                onClick={submit}
                data-test='form--create-webhook__button--submit'
              >
                Create
              </Button>
            </HStack>
          </ModalBody>
        </ModalContainer>
      </Form>
    </ModalWrapper>
  );
};

export interface FormCreateWebhookData {
  url: string;
  'api-version': ApiVersion;
  'api-key-inflection': ApiKeyInflection;
  'enabled-events': EventName[];
}
